/**
 * @description
 * Settings page for system configuration and preferences.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date 2025-01-06
 * @see admin/src/frontend/pages/Settings.test.tsx
 */

import { useState } from "react";
import { useAuth } from "../hooks/useAuth";
import { SettingsNavigation } from "./Settings/SettingsNavigation";
import { GenericSettingsPanel } from "./Settings/GenericSettingsPanel";
import { StripeSettings } from "./Settings/StripeSettings";
import { EmailSettings } from "./Settings/EmailSettings";
import { PlatformSettings } from "./Settings/PlatformSettings";
import { useSettingsData } from "./Settings/hooks/useSettingsData";
import { SettingsSection } from "./Settings/types";

/**
 * Settings page component
 */
export function Settings() {
  const { user } = useAuth();
  const [activeSection, setActiveSection] =
    useState<SettingsSection>("general");
  const { settings, loading, refetch } = useSettingsData();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  const renderSettingsContent = () => {
    if (activeSection === "stripe") {
      return <StripeSettings onRefresh={refetch} />;
    }

    if (activeSection === "email") {
      return <EmailSettings onRefresh={refetch} />;
    }

    if (activeSection === "platform_settings") {
      return <PlatformSettings onRefresh={refetch} />;
    }

    // Generic settings for: general, cloudflare, dns, deployment, vibesdk, vscode_lens, anthropic
    return (
      <GenericSettingsPanel
        settings={settings}
        category={activeSection}
        onUpdate={refetch}
        user={user}
      />
    );
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Settings</h1>
        <p className="text-muted-foreground">
          Manage your system configuration and preferences
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-4">
        {/* Settings Navigation */}
        <div className="lg:col-span-1">
          <SettingsNavigation
            activeSection={activeSection}
            onSectionChange={setActiveSection}
          />
        </div>

        {/* Settings Content */}
        <div className="lg:col-span-3">
          <div className="rounded-lg border bg-card p-6">
            {activeSection !== "platform_settings" && (
              <h2 className="text-lg font-semibold mb-4 capitalize">
                {activeSection.replace(/_/g, " ")} Settings
              </h2>
            )}

            {renderSettingsContent()}
          </div>
        </div>
      </div>
    </div>
  );
}

/*
 * © 2025 Byte Inc. All rights reserved.
 * This code is the confidential and proprietary information of Byte Inc.
 * You shall not disclose such Confidential Information and shall use it only in
 * accordance with the terms of the license agreement you entered into with Byte Inc.
 */
